// src/context/AuthContext.tsx
import React, { createContext, useContext, useState } from 'react';
import { getCurrentApiUrl, getCurrentBaseUrl } from 'config/server.config';
import { User, LoginResponse, AuthState } from '../types/auth.types';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {authService} from '../services/auth.service';
import { useDispatch } from 'react-redux';
import type { AppDispatch, RootState } from '../store';
import { logoutAuth } from '../store/authReducer';
interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, remember: boolean, twoFactorToken?: string) => Promise<LoginResponse>;
  logout: () => void;
  getCurrentPhase: () => string | undefined;
  isFullOnboarding: () => boolean;
  updateUserPhoto: (photoURL: string) => Promise<string | void>;
  loadProfileImage: (photoURL: string | undefined) => Promise<string | null>;
  canAccessCryptoWallets: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<AppDispatch>();
  const [authState, setAuthState] = useState<AuthState>(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    return {
      user,
      token,
      isAuthenticated: !!token && !!user,
      isLoading: false,
      error: null
    };
  });

  const login = async (email: string, password: string, remember: boolean, twoFactorToken?: string): Promise<LoginResponse> => {
    try {
      // Limpiar cualquier dato de usuario anterior y caché
      // clearUserDataAndCache();

      const data: LoginResponse = await authService.login(email, password, remember, twoFactorToken);

      // After successful login, fetch user photo
      // if (data.user && data.token) {
      //   try {
      //     const photoResponse = await fetch('https://api.v2.belobaba.io/api/users/photo', {
      //       headers: {
      //         'Authorization': `Bearer ${data.token}`
      //       }
      //     });

      //     if (photoResponse.ok) {
      //       const photoData = await photoResponse.json();
      //       if (photoData.success && photoData.data.photoURL) {
      //         data.user.profileImage = photoData.data.photoURL;
      //         console.log('Photo URL added to user:', data.user.profileImage);
      //       }
      //     }
      //   } catch (photoError) {
      //     console.error('Error fetching photo:', photoError);
      //   }
      // }

      // Limpiar datos anteriores antes de guardar los nuevos
      // [localStorage, sessionStorage].forEach(storage => {
      //   storage.removeItem('dashboardData');
      //   storage.removeItem('dashboardDataTimestamp');
      //   storage.removeItem('userWallets');
      //   storage.removeItem('userWallets-timestamp');
      // });

      // Invalidar todas las queries para forzar recargas frescas
      // queryClient.clear();

      const storage = remember ? localStorage : sessionStorage;
      storage.setItem('token', data.token);
      // storage.setItem('user', JSON.stringify(data.user));
      console.log('User data saved to storage:', data.user);

      setAuthState({
        user: data.user,
        token: data.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

      // OPTIMIZATION: Zenus sync movido al dashboard para no bloquear login
      // La sincronización ocurrirá automáticamente cuando se cargue el dashboard
      // Esto reduce el tiempo de login en ~1-2 segundos

      return data;
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'An error occurred'
      });
      throw error;
    }
  };

  // Función para limpiar datos de usuario y caché
  const clearUserDataAndCache = () => {
    // Limpiar datos de usuario y token
    [localStorage, sessionStorage].forEach(storage => {
      storage.removeItem('token');
      storage.removeItem('user');
      
      // Limpiar cachés específicas
      storage.removeItem('dashboardData');
      storage.removeItem('dashboardDataTimestamp');
      storage.removeItem('userWallets');
      storage.removeItem('userWallets-timestamp');
      
      // Limpiar caché de imágenes de perfil
      Object.keys(storage).forEach(key => {
        if (key.startsWith('profile-image-') || 
            key.startsWith('tron-wallet-') || 
            key.startsWith('btc-wallet-') || 
            key.startsWith('eth-wallet-') || 
            key.startsWith('usdt-erc20-wallet-') || 
            key.startsWith('bnb-wallet-')) {
          storage.removeItem(key);
          storage.removeItem(`${key}-timestamp`);
        }
      });
    });
    
    // Limpiar todas las queries de React Query
    queryClient.clear();
  };

  // Añadir esta función dentro de la definición de AuthProvider
  const canAccessCryptoWallets = () => {
    console.log("Current phase check:", authState.user?.currentPhase);
    
    // Si el usuario no está autenticado, no puede acceder
    if (!authState.isAuthenticated) return false;
    
    // Si el usuario es ONBOARDING_COMPLETE o ONBOARDING_COMPLETE_FAST, puede acceder
    const completePhases = ['ONBOARDING_COMPLETE', 'ONBOARDING_COMPLETE_FAST'];
    const hasAccess = completePhases.includes(authState.user?.currentPhase || '');
    
    console.log("Access result:", hasAccess);
    return hasAccess;
  };

  const logout = () => {
    // Limpiar datos y caché
    clearUserDataAndCache();
    dispatch(logoutAuth())
    authService.logout()
    // Actualizar estado
    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });
    
    // Redirigir al home
    window.location.href = '/';
  };

  const getCurrentPhase = (): string | undefined => {
    return authState.user?.currentPhase;
  };

  // Función simplificada que verifica si el usuario tiene onboarding completo
  // Esto solo devuelve true para ONBOARDING_COMPLETE (no para FAST)
  const isFullOnboarding = (): boolean => {
    return authState.user?.currentPhase === 'ONBOARDING_COMPLETE';
  };

  // Cargar la imagen de perfil desde el servidor con autorización
  const loadProfileImage = async (photoURL: string | undefined): Promise<string | null> => {
    if (!photoURL) return null;
    
    // Si la URL no comienza con /uploads, no es una imagen de nuestro servidor
    if (!photoURL.startsWith('/uploads')) {
      return photoURL;
    }
    
    // Extraer el nombre del archivo de la ruta
    const fileName = photoURL.split('/').pop();
    if (!fileName) return null;
    
    // Verificar si tenemos la imagen en caché
    const cacheKey = `profile-image-${fileName}`;
    const cachedImage = localStorage.getItem(cacheKey);
    const cachedTimestamp = localStorage.getItem(`${cacheKey}-timestamp`);
    
    // Usar caché si existe y no es muy antigua (menos de 1 hora)
    if (cachedImage && cachedTimestamp) {
      const timestamp = parseInt(cachedTimestamp, 10);
      const now = Date.now();
      if (now - timestamp < 60 * 60 * 1000) { // 1 hora
        console.log('Usando imagen en caché:', fileName);
        return cachedImage;
      }
    }
    
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return null;
    
    console.log('Cargando imagen de perfil desde el servidor:', fileName);
    
    // Realizar la solicitud al endpoint de vista previa con autorización
    try {
      const response = await fetch(`${getCurrentApiUrl()}/users/preview/${fileName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        console.error(`Error al cargar imagen: ${response.status}`, await response.text());
        throw new Error(`Failed to load image: ${response.status}`);
      }
      
      // Convertir la respuesta a blob
      const blob = await response.blob();
      
      // Verificar que el blob tenga contenido
      if (blob.size === 0) {
        console.error('Se recibió un blob vacío');
        throw new Error('Empty image data received');
      }
      
      console.log(`Imagen cargada exitosamente: ${fileName}, tamaño: ${(blob.size/1024).toFixed(2)}KB, tipo: ${blob.type}`);
      
      // Crear una URL para el blob
      const objectUrl = URL.createObjectURL(blob);
      
      // Almacenar en caché para futuras referencias
      localStorage.setItem(cacheKey, objectUrl);
      localStorage.setItem(`${cacheKey}-timestamp`, Date.now().toString());
      
      return objectUrl;
    } catch (error) {
      console.error('Error cargando imagen de perfil:', error);
      
      // Si falla, intentar una alternativa: acceder directamente a través de S3 o un CDN si está disponible
      try {
        // Intentar con el endpoint público si existe
        console.log('Intentando endpoint público alternativo');
        const publicResponse = await fetch(`${getCurrentApiUrl()}/users/test-s3-direct/${fileName}`);
        
        if (publicResponse.ok) {
          const blob = await publicResponse.blob();
          const objectUrl = URL.createObjectURL(blob);
          
          // Almacenar en caché
          localStorage.setItem(cacheKey, objectUrl);
          localStorage.setItem(`${cacheKey}-timestamp`, Date.now().toString());
          
          console.log('Imagen cargada exitosamente mediante ruta alternativa');
          return objectUrl;
        }
      } catch (altError) {
        console.error('Error en intento alternativo:', altError);
      }
      
      return null;
    }
  };

  // Método simplificado para actualizar la foto del usuario
  const updateUserPhoto = async (photoURL: string) => {
    try {
      console.log('Updating user photo with URL:', photoURL);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      // Actualizar directamente el usuario con cualquier URL válida proporcionada
      if (photoURL && authState.user) {
        const updatedUser = { 
          ...authState.user, 
          profileImage: photoURL
        };
        
        setAuthState(prev => ({
          ...prev,
          user: updatedUser
        }));

        const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
        storage.setItem('user', JSON.stringify(updatedUser));
        console.log('User profile image updated in state');
        return photoURL; // Importante: retornar la URL
      }
      
      // Si llegamos aquí sin una URL válida o sin usuario, lanzar error
      throw new Error('Invalid photo URL or user not authenticated');
    } catch (error) {
      console.error('Error updating profile photo:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user: authState.user,
    token: authState.token,
    isAuthenticated: authState.isAuthenticated,
    login,
    logout,
    getCurrentPhase,
    isFullOnboarding,
    updateUserPhoto,
    loadProfileImage,
    canAccessCryptoWallets
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;